import { createApiClient } from '../utils/axiosConfig';
import { getApiBaseUrl } from '../utils/apiConfig';

// Use base server URL - normalize to remove trailing /api if present
let API_BASE_URL = getApiBaseUrl();
if (API_BASE_URL.endsWith('/api')) {
  API_BASE_URL = API_BASE_URL.slice(0, -4);
}

// Create axios instance with default config and auth error handling
const apiClient = createApiClient(API_BASE_URL);

// Support Ticket interfaces
export interface SupportTicket {
  _id: string;
  ticketId: string;
  title: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: string;
  assignedTo?: string;
  responses: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTicketRequest {
  title: string;
  description: string;
  category: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
}

export interface TicketsResponse {
  success: boolean;
  tickets: SupportTicket[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// FAQ interfaces
export interface FAQ {
  _id: string;
  question: string;
  answer: string;
  category: string;
  helpful: number;
  notHelpful: number;
  tags: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FAQsResponse {
  success: boolean;
  faqs: FAQ[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Feedback interfaces
export interface Feedback {
  _id: string;
  userId: string;
  rating: number;
  likedMost?: string;
  improvements?: string;
  additionalComments?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SubmitFeedbackRequest {
  rating: number;
  likedMost?: string;
  improvements?: string;
  additionalComments?: string;
}

// Support Ticket API
export const getSupportTickets = async (
  status?: string,
  priority?: string,
  category?: string,
  page: number = 1,
  limit: number = 100
): Promise<TicketsResponse> => {
  const params: any = { page, limit };
  if (status && status !== 'all') params.status = status;
  if (priority && priority !== 'all') params.priority = priority;
  if (category && category !== 'all') params.category = category;

  const response = await apiClient.get('/api/support/tickets', { params });
  return response.data;
};

export const getSupportTicket = async (id: string): Promise<{ success: boolean; ticket: SupportTicket }> => {
  const response = await apiClient.get(`/api/support/tickets/${id}`);
  return response.data;
};

export const createSupportTicket = async (data: CreateTicketRequest): Promise<{ success: boolean; message: string; ticket: SupportTicket }> => {
  const response = await apiClient.post('/api/support/tickets', data);
  return response.data;
};

export const updateSupportTicket = async (
  id: string,
  data: Partial<CreateTicketRequest & { status?: string }>
): Promise<{ success: boolean; message: string; ticket: SupportTicket }> => {
  const response = await apiClient.put(`/api/support/tickets/${id}`, data);
  return response.data;
};

// FAQ API
export const getFAQs = async (
  category?: string,
  search?: string,
  page: number = 1,
  limit: number = 100
): Promise<FAQsResponse> => {
  const params: any = { page, limit };
  if (category && category !== 'all') params.category = category;
  if (search) params.search = search;

  const response = await apiClient.get('/api/support/faqs', { params });
  return response.data;
};

export const rateFAQ = async (
  faqId: string,
  isHelpful: boolean
): Promise<{ success: boolean; message: string; faq: FAQ }> => {
  const response = await apiClient.post(`/api/support/faqs/${faqId}/rate`, { isHelpful });
  return response.data;
};

// Feedback API
export const submitFeedback = async (data: SubmitFeedbackRequest): Promise<{ success: boolean; message: string; feedback: Feedback }> => {
  const response = await apiClient.post('/api/support/feedback', data);
  return response.data;
};

export const getFeedback = async (): Promise<{ success: boolean; feedbacks: Feedback[] }> => {
  const response = await apiClient.get('/api/support/feedback');
  return response.data;
};

